import React from "react";
import { Link } from "react-router-dom";
import { WaveIcon, Sparkle, Squiggle, ScribbleCircle, Blob, BnGlyph } from "@/components/landing/Decorations";
import { Waveform } from "@/components/landing/Waveform";

// Two-column auth shell: form on left, brand panel on right
export const AuthShell = ({ children, title, subtitle, side = "right" }) => {
    return (
        <div className="min-h-screen bg-cream-100 flex">
            {/* Form */}
            <div className="flex-1 flex flex-col p-6 sm:p-10 lg:p-14 relative overflow-hidden">
                <Link to="/" data-testid="auth-back-home" className="inline-flex items-center gap-2 self-start group">
                    <WaveIcon color="#FF5B3E" className="w-5 h-3.5"/>
                    <span className="font-display text-[20px] font-semibold tracking-tight text-ink group-hover:text-coral transition-colors">Audo</span>
                </Link>

                <div className="flex-1 flex items-center justify-center py-10">
                    <div className="w-full max-w-[420px]">
                        <h1 className="h-display text-[40px] sm:text-[52px] font-medium tracking-tight text-ink leading-[1.05] mb-3">
                            {title}
                        </h1>
                        {subtitle && <p className="text-[15px] text-ink-muted leading-relaxed mb-9">{subtitle}</p>}
                        {children}
                    </div>
                </div>

                <Squiggle color="#FFD23F" className="absolute bottom-12 left-12 w-24 opacity-60 animate-float-slow"/>
                <Sparkle color="#FF5B3E" className="absolute top-1/3 left-8 w-7 h-7 opacity-70 animate-float-fast"/>
            </div>

            {/* Brand panel */}
            <div className={`hidden lg:flex flex-1 bg-ink-pill text-cream-100 relative overflow-hidden ${side === "left" ? "order-first" : ""}`}>
                <div className="absolute inset-0 opacity-20">
                    <Waveform bars={80} color="#FF5B3E" height={140} className="absolute inset-x-0 bottom-1/3" playing={true}/>
                </div>
                <Blob color="#FF4FA3" className="absolute -top-20 -right-20 w-72 h-72 opacity-40 animate-float-slow"/>
                <ScribbleCircle color="#FFD23F" className="absolute bottom-16 left-16 w-32 h-32 opacity-60 animate-float-fast"/>
                <BnGlyph glyph="অ" className="absolute top-1/4 right-1/4 text-[280px] leading-none font-bold opacity-[0.07] select-none" color="#FFD23F"/>

                <div className="relative z-10 m-auto max-w-md p-12">
                    <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-coral mb-6">Built for Bengali newsrooms</div>
                    <p className="font-display text-[44px] leading-[1.05] font-medium tracking-tight">
                        Turn any article into <span className="italic-display text-coral">beautiful</span> Bengali audio in 30 seconds.
                    </p>
                    <p className="bn mt-8 text-[18px] text-cream-100/70 leading-relaxed">
                        ২,০০০+ পোর্টাল ইতিমধ্যেই Audo ব্যবহার করছে।
                    </p>
                    <div className="mt-12 flex items-center gap-4">
                        <div className="flex -space-x-2">
                            {["#FF5B3E", "#3D5BFF", "#2EE8B5", "#FFD23F"].map((c, i) => (
                                <div key={i} className="w-9 h-9 rounded-full border-2 border-ink-pill" style={{ background: c }}/>
                            ))}
                        </div>
                        <span className="text-[13px] text-cream-100/70">Trusted by top news portals, blogs, and content creators.</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const GoogleButton = ({ label = "Continue with Google" }) => {
    const onClick = () => {
        alert("Google Login is currently disabled");
    };
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid="google-login-btn"
            className="w-full inline-flex items-center justify-center gap-3 bg-white border border-cream-300 hover:border-ink rounded-full py-3 px-4 text-[14.5px] font-semibold text-ink hover:shadow-soft transition-all"
        >
            <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {label}
        </button>
    );
};

export const Divider = ({ children = "or" }) => (
    <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-cream-300"/>
        <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-ink-muted">{children}</span>
        <div className="flex-1 h-px bg-cream-300"/>
    </div>
);
